/**
 * Task Estimation AI Service - Phase 4 Architecture
 *
 * Encapsulates AI-powered task estimation, duration estimation, and bulk estimation.
 * Extracted from TaskEstimationService to reduce single-responsibility violations.
 */

import { typedSupabase } from '@/lib/typed-supabase';
import { logger } from '@/lib/logger';
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from '@/services/core/service-registry';
import type {
  CreateTaskEstimationRequest,
  EstimationFactor,
  EstimationMethod,
  TaskEstimation,
} from "@/types/time-management";
import { TaskEstimationCrudService } from './task-estimation-crud.service';
import { TaskEstimationHistoricalService } from './task-estimation-historical.service';

export class TaskEstimationAIService implements IService {
  public readonly name = "TaskEstimationAIService";
  private readonly supabase = typedSupabase;
  private crudService: TaskEstimationCrudService;
  private historicalService: TaskEstimationHistoricalService;

  constructor(crudService: TaskEstimationCrudService, historicalService: TaskEstimationHistoricalService) {
    this.crudService = crudService;
    this.historicalService = historicalService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ TaskEstimationAIService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskEstimationAIService disposed");
  }

  async estimateTaskDuration(
    taskId: string,
    method: EstimationMethod,
    factors?: EstimationFactor[]
  ): Promise<ServiceResponse<TaskEstimation>> {
    try {
      const { data: task, error: taskError } = await this.supabase
        .from("todos")
        .select("*")
        .eq("id", taskId)
        .single();

      if (taskError) {
        return createErrorResponse("Task not found for estimation");
      }

      let estimation: TaskEstimation;

      switch (method) {
        case "ai_suggested": {
          const aiResult = await this.generateAIEstimation(
            task.title,
            task.description ?? undefined,
            task.category_id ?? undefined,
            task.created_by ?? ''
          );

          if (aiResult.error) {
            return createErrorResponse(aiResult.error);
          }

          estimation = {
            id: `est_${Date.now()}`,
            task_id: taskId,
            estimated_duration: aiResult.data!.estimatedDuration,
            confidence_level: aiResult.data!.confidenceLevel,
            estimation_method: "ai_suggested",
            factors: aiResult.data!.factors,
            notes: aiResult.data!.reasoning,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          break;
        }

        case "historical": {
          const historicalResult = await this.estimateFromHistoricalData(
            task.title,
            task.created_by ?? ''
          );

          if (historicalResult.error) {
            return createErrorResponse(historicalResult.error);
          }

          estimation = historicalResult.data!;
          break;
        }

        case "manual": {
          estimation = {
            id: `est_${Date.now()}`,
            task_id: taskId,
            estimated_duration: this.calculateManualEstimation(factors ?? []),
            confidence_level: "medium",
            estimation_method: "manual",
            factors: factors ?? [],
            notes: "Manual estimation based on provided factors",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          break;
        }

        default: {
          return createErrorResponse(`Unknown estimation method: ${method}`);
        }
      }

      const createResult = await this.crudService.createEstimation(estimation as CreateTaskEstimationRequest);
      return createResult;
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async generateAIEstimation(
    taskTitle: string,
    description?: string,
    category?: string,
    _userId?: string
  ): Promise<
    ServiceResponse<{
      estimatedDuration: number;
      confidenceLevel: "low" | "medium" | "high";
      factors: EstimationFactor[];
      reasoning: string;
    }>
  > {
    try {
      const complexity = this.analyzeTaskComplexity(taskTitle, description);
      const baseEstimate = this.getBaseEstimate(taskTitle, category);

      const factors: EstimationFactor[] = [
        {
          type: "complexity",
          weight: 0.4,
          value: complexity,
          description: `Task complexity level: ${complexity}/10`,
        },
        {
          type: "similar_tasks",
          weight: 0.3,
          value: baseEstimate.similarity,
          description: `Based on similar tasks with ${baseEstimate.similarity * 100}% similarity`,
        },
      ];

      const estimatedDuration = Math.round(
        baseEstimate.duration * (1 + complexity / 10)
      );

      const confidenceLevel = this.calculateConfidenceLevel(
        complexity,
        baseEstimate.similarity
      );

      return createSuccessResponse({
        estimatedDuration,
        confidenceLevel,
        factors,
        reasoning: `AI estimation based on task complexity (${complexity}/10) and similarity to previous tasks. Base estimate: ${baseEstimate.duration} minutes.`,
      });
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  async bulkEstimate(
    tasks: Array<{
      taskId: string;
      title: string;
      description?: string;
      category?: string;
    }>,
    _userId: string
  ): Promise<ServiceResponse<TaskEstimation[]>> {
    try {
      const results: TaskEstimation[] = [];
      const errors: string[] = [];

      for (const task of tasks) {
        const result = await this.estimateTaskDuration(
          task.taskId,
          "ai_suggested"
        );

        if (!result.success) {
          errors.push(`${task.title}: ${result.error}`);
        } else if (result.data) {
          results.push(result.data);
        }
      }

      if (errors.length > 0) {
        return createErrorResponse(`${errors.length} tasks failed: ${errors.join(", ")}`);
      }

      return createSuccessResponse(results);
    } catch (error) {
      return createErrorResponse(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  private async estimateFromHistoricalData(
    taskTitle: string,
    userId: string
  ): Promise<ServiceResponse<TaskEstimation>> {
    const similarTasks = await this.historicalService.getSimilarTasks(taskTitle, userId, 3);

    if (
      similarTasks.error ||
      !similarTasks.data ||
      similarTasks.data.length === 0
    ) {
      return createErrorResponse("No similar tasks found for historical estimation");
    }

    const avgDuration =
      similarTasks.data.reduce((sum, task) => sum + task.actualDuration, 0) /
      similarTasks.data.length;
    const avgSimilarity =
      similarTasks.data.reduce((sum, task) => sum + task.similarity, 0) /
      similarTasks.data.length;

    return createSuccessResponse({
      id: `est_${Date.now()}`,
      task_id: "",
      estimated_duration: Math.round(avgDuration),
      confidence_level:
        avgSimilarity > 0.7 ? "high" : avgSimilarity > 0.5 ? "medium" : "low",
      estimation_method: "historical",
      factors: [
        {
          type: "similar_tasks",
          weight: 1.0,
          value: avgSimilarity,
          description: `Based on ${similarTasks.data.length} similar tasks`,
        },
      ],
      notes: `Historical estimation based on ${similarTasks.data.length} similar tasks`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  private calculateManualEstimation(factors: EstimationFactor[]): number {
    if (factors.length === 0) {return 30;}

    const weightedSum = factors.reduce((sum, factor) => {
      return sum + factor.value * factor.weight * 60;
    }, 0);

    return Math.max(5, Math.round(weightedSum));
  }

  private analyzeTaskComplexity(title: string, description?: string): number {
    const text = `${title} ${description ?? ""}`.toLowerCase();
    let complexity = 5;

    const complexKeywords = [
      "integrate",
      "implement",
      "refactor",
      "optimize",
      "design",
      "architecture",
    ];
    const simpleKeywords = ["update", "fix", "change", "add", "remove"];

    complexKeywords.forEach((keyword) => {
      if (text.includes(keyword)) {complexity += 1;}
    });

    simpleKeywords.forEach((keyword) => {
      if (text.includes(keyword)) {complexity -= 0.5;}
    });

    return Math.max(1, Math.min(10, complexity));
  }

  private getBaseEstimate(
    title: string,
    _category?: string
  ): { duration: number; similarity: number } {
    const wordCount = title.split(" ").length;
    const baseDuration = Math.max(15, wordCount * 5);

    return {
      duration: baseDuration,
      similarity: 0.5,
    };
  }

  private calculateConfidenceLevel(
    complexity: number,
    similarity: number
  ): "low" | "medium" | "high" {
    if (complexity <= 3 && similarity > 0.7) {return "high";}
    if (complexity <= 6 && similarity > 0.5) {return "medium";}
    return "low";
  }
}
